import { addDays, DateOnly } from '@if/core/date'
import { formatCurrencyNoDecimals, formatPercentage, formatUtcIsoDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  Caption,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  DataField,
  type ErrorOverrides,
  FieldSectionTitle,
  type FormDialogContentContext,
  StatCard,
  StepProgressMeter,
  StrongValue,
  SubsectionTitle,
  useDebouncedWatch,
  useFormDialog,
} from '@if/mui'
import { Box, Card, CardContent, Divider, Skeleton, Stack, styled } from '@mui/material'
import { useCreateCustomBuydownPlan, useCustomBuydownTermRates } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { type BorrowerContext, useBorrowerContext } from '@src/hooks/borrower-context-hooks'
import { CustomBuydownPlan, CustomBuydownTimeline } from '@src/types/CustomBuydown'
import type { AxiosError } from 'axios'
import { Lock, Percent } from 'lucide-react'
import { useMemo } from 'react'
import { useWatch, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PromoPlanErrorCode } from '../promoPlanErrorCodes'

const MIN_CONTRIBUTION = 0.005
const MAX_CONTRIBUTION = 0.3
const MIN_LOAN_AMOUNT = 500
const ADMIN_FEE_RATE = 0.06
const COST_REFERENCE_AMOUNT = 10000
const CONTRIBUTION_MARKS = [0, 5, 10, 15, 20, 25, 30].map((value) => ({ value, label: `${value}%` }))
const TERM_SKELETON_COUNT = 5
const TERM_TILE_HEIGHT = 72

const APPROVAL_MODEL = { P_MIN: 0.3, P_MAX: 0.8, LAMBDA: 10 } as const
const conversionProbability = (contribution: number): number =>
  APPROVAL_MODEL.P_MIN + (APPROVAL_MODEL.P_MAX - APPROVAL_MODEL.P_MIN) * (1 - Math.exp(-APPROVAL_MODEL.LAMBDA * contribution))
const APPROVAL_TIERS = [
  { threshold: 0, level: 'marginal', color: 'error' },
  { threshold: 0.015, level: 'low', color: 'warning' },
  { threshold: 0.03, level: 'moderate', color: 'warning' },
  { threshold: 0.05, level: 'good', color: 'info' },
  { threshold: 0.1, level: 'high', color: 'info' },
  { threshold: 0.15, level: 'optimal', color: 'success' },
  { threshold: 0.2, level: 'veryHigh', color: 'success' },
] as const

const previewPlanCode = (contribution: number) => `FLASH${Math.round(contribution * 10000)}/${ADMIN_FEE_RATE * 100}`

const toActiveFromWire = (activeFrom: DateOnly): string =>
  activeFrom.getTime() === DateOnly.today().getTime() ? formatUtcIsoDateTime(new Date()) : formatUtcIsoDateTime(activeFrom.toDate())

const ContributionCardRoot = styled(Card)(({ theme }) => ({
  '&&&': { backgroundColor: theme.vars.palette.background.default },
  boxShadow: 'none',
}))

const TimelineCard = styled(Card)(({ theme }) => ({
  '&&&': { backgroundColor: theme.vars.palette.accent.background },
  borderColor: theme.vars.palette.accent.border,
  boxShadow: 'none',
}))

const TimelineRow = ({ dotColor, range, label }: { dotColor: string; range: string; label: string }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
    <StrongValue sx={{ minWidth: 190, flexShrink: 0 }}>{range}</StrongValue>
    <Caption sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{label}</Caption>
  </Stack>
)

const buildCreatePlanErrorOverrides = (timeline: CustomBuydownTimeline): ErrorOverrides => ({
  conflict: (descriptor, t) => {
    const raw = descriptor.raw as AxiosError<PromoPlanErrorCode>
    const code = raw.response?.data

    switch (code) {
      case PromoPlanErrorCode.WindowOverlap:
        return {
          role: 'contextual',
          message: t('customBuydown.errors.windowOverlap', {
            planCode: timeline.active?.label,
            expiresOn: timeline.active?.expiresOn ? new DateOnly(timeline.active.expiresOn).toString() : '',
          }),
        }
      case PromoPlanErrorCode.ActivePlanNoEndDate:
        return {
          role: 'contextual',
          message: t('customBuydown.errors.activePlanNoEndDate', { planCode: timeline.active?.label }),
        }
      case PromoPlanErrorCode.ScheduledPlanAlreadyExists:
        return {
          role: 'contextual',
          message: t('customBuydown.errors.scheduledPlanAlreadyExists', { planCode: timeline.scheduled?.label }),
        }
      case PromoPlanErrorCode.NewPlanOverlapsScheduledPlan:
        return {
          role: 'contextual',
          message: t('customBuydown.errors.newPlanOverlapsScheduledPlan', {
            planCode: timeline.scheduled?.label,
            activeFrom: timeline.scheduled?.activeFrom ? new DateOnly(timeline.scheduled.activeFrom).toString() : '',
          }),
        }
      default:
        return undefined
    }
  },
})

const createPlanSchema = () =>
  yup.object({
    contribution: yup.percent().required().min(MIN_CONTRIBUTION).max(MAX_CONTRIBUTION),
    maxFinancingAmount: yup.number().min(MIN_LOAN_AMOUNT).defined().nullable(),
    activeFrom: yup.dateOnly().required().min(DateOnly.today()),
    expiresOn: yup.dateOnly().defined().nullable(),
  })

type CreatePlanFormValues = yup.InferSubmit<ReturnType<typeof createPlanSchema>>

type CreatePlanCtx = FormDialogContentContext<CreatePlanFormValues, CustomBuydownPlan | null, CustomBuydownPlan>

const RatePreview =({
  merchantId,
  control,
  context,
}: {
  merchantId: string
  control: UseFormReturn<CreatePlanFormValues>['control']
  context: BorrowerContext
}) => {
  const { t } = useTranslation()

  const debouncedContribution = useDebouncedWatch({ control, name: 'contribution' })
  const debouncedMaxAmount = useDebouncedWatch({ control, name: 'maxFinancingAmount' })
  const canPreview = debouncedContribution >= MIN_CONTRIBUTION && debouncedContribution <= MAX_CONTRIBUTION

  const { data: rates, isLoading } = useCustomBuydownTermRates(
    merchantId,
    debouncedContribution,
    ADMIN_FEE_RATE,
    debouncedMaxAmount ?? undefined,
  )
  const showSkeletons = !canPreview || (isLoading && !rates)

  return (
    <Box>
      <FieldSectionTitle>
        {t('customBuydown.createDialog.availableTerms', { context, rate: `${formatPercentage(debouncedContribution * 100)}%` })}
      </FieldSectionTitle>
      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1.5 }}>
        {showSkeletons
          ? Array.from({ length: TERM_SKELETON_COUNT }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={TERM_TILE_HEIGHT} />
            ))
          : rates?.map((rate) => (
              <StatCard
                key={rate.termMonths}
                size="sm"
                accent
                label={t('customBuydown.termLabel', { months: rate.termMonths })}
                value={`${formatPercentage(rate.promoRate * 100)}%`}
                sx={{ height: TERM_TILE_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              />
            ))}
      </Box>
      <Caption sx={{ display: 'block', mt: 1 }}>{t('customBuydown.createDialog.ratesCaption')}</Caption>
    </Box>
  )
}

const ContributionCard =({
  control,
  contribution,
  context,
}: {
  control: UseFormReturn<CreatePlanFormValues>['control']
  contribution: number
  context: BorrowerContext
}) => {
  const { t } = useTranslation()
  const probability = conversionProbability(contribution)
  const steps = useMemo(
    () =>
      APPROVAL_TIERS.map(({ threshold, level, color }) => ({
        from: conversionProbability(threshold),
        color,
        label: t(`customBuydown.approval.${level}`),
        description: t(`customBuydown.approvalHint.${level}`),
      })),
    [t],
  )

  return (
    <ContributionCardRoot>
      <CardContent>
        <Stack spacing={2.5} divider={<Divider />}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <FieldSectionTitle>{t('customBuydown.contributionLabel')}</FieldSectionTitle>
                <Caption sx={{ display: 'block' }}>{t('customBuydown.contributionCardSubtitle', { context })}</Caption>
              </Box>
              <ControlledPercentField control={control} name="contribution" size="small" sx={{ width: 110 }} />
            </Stack>
            <Box sx={{ px: 1, mt: 2 }}>
              <ControlledPercentSlider
                control={control}
                name="contribution"
                min={MIN_CONTRIBUTION * 100}
                max={MAX_CONTRIBUTION * 100}
                step={0.01}
                marks={CONTRIBUTION_MARKS}
              />
            </Box>
          </Box>

          <StepProgressMeter
            title={t('customBuydown.createDialog.approvalOutlookTitle')}
            steps={steps}
            value={probability}
            min={APPROVAL_MODEL.P_MIN}
            max={APPROVAL_MODEL.P_MAX}
            sx={{ '& .MuiLinearProgress-root': { height: 6, mx: 1, width: 'auto' } }}
          />
        </Stack>
      </CardContent>
    </ContributionCardRoot>
  )
}

interface PlanWindow {
  activeFrom: string
  expiresOn: string | null
}

const hasTimelineGap = (active: PlanWindow | null, scheduled: PlanWindow | null): boolean => {
  if (!active?.expiresOn || !scheduled) return false
  return new DateOnly(addDays(new DateOnly(active.expiresOn), 1)).getTime() < new DateOnly(scheduled.activeFrom).getTime()
}

const PlanTimelineCard = ({
  active,
  scheduled,
  activeFrom,
  expiresOn,
}: {
  active: CustomBuydownPlan | null
  scheduled: CustomBuydownPlan | null
  activeFrom: DateOnly
  expiresOn: DateOnly | null
}) => {
  const { t } = useTranslation()

  const newPlan: PlanWindow = { activeFrom: activeFrom.toString(), expiresOn: expiresOn ? expiresOn.toString() : null }
  const effectiveActive = active ?? (scheduled ? newPlan : null)
  const effectiveScheduled = active ? newPlan : scheduled

  const titleKey = !active && !scheduled
    ? 'customBuydown.createDialog.timelineTitle'
    : hasTimelineGap(effectiveActive, effectiveScheduled)
      ? 'customBuydown.createDialog.timelineTitleGap'
      : 'customBuydown.createDialog.timelineTitleSeamless'

  return (
    <TimelineCard sx={{ mt: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <SubsectionTitle>{t(titleKey)}</SubsectionTitle>
          <Stack spacing={1}>
            {active && (
              <TimelineRow
                dotColor="text.secondary"
                range={`${new DateOnly(active.activeFrom).toString()} → ${
                  active.expiresOn ? new DateOnly(active.expiresOn).toString() : t('customBuydown.createDialog.timelineNoEndDate')
                }`}
                label={t('customBuydown.createDialog.timelineAppliesLabel', { planCode: active.label })}
              />
            )}
            <TimelineRow
              dotColor="primary.main"
              range={`${activeFrom.toString()} → ${
                expiresOn ? expiresOn.toString() : t('customBuydown.createDialog.timelineNoEndDate')
              }`}
              label={t('customBuydown.createDialog.timelineNewPlanApplies')}
            />
            {!active && scheduled && (
              <TimelineRow
                dotColor="text.secondary"
                range={`${new DateOnly(scheduled.activeFrom).toString()} → ${
                  scheduled.expiresOn
                    ? new DateOnly(scheduled.expiresOn).toString()
                    : t('customBuydown.createDialog.timelineNoEndDate')
                }`}
                label={t('customBuydown.createDialog.timelineAppliesLabel', { planCode: scheduled.label })}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </TimelineCard>
  )
}

const CreatePlanDialogContent = ({
  ctx,
  merchantId,
  timeline,
  context,
}: {
  ctx: CreatePlanCtx
  merchantId: string
  timeline: CustomBuydownTimeline
  context: BorrowerContext
}) => {
  const { t } = useTranslation()
  const { control } = ctx.form
  const { active, scheduled } = timeline

  const contribution = useWatch({ control, name: 'contribution' })
  const maxFinancingAmount = useWatch({ control, name: 'maxFinancingAmount' })
  const activeFrom = useWatch({ control, name: 'activeFrom' })
  const expiresOn = useWatch({ control, name: 'expiresOn' })

  return (
    <Stack spacing={2.5} divider={<Divider />}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 2,
          '& > *:not(:last-child)': { borderRight: '1px solid', borderColor: 'divider', pr: 2 },
        }}
      >
        <DataField label={t('customBuydown.planCode')} value={previewPlanCode(contribution)} />
        <DataField label={t('customBuydown.yourFeeRate')} value={`${formatPercentage(contribution * 100)}%`} />
        <DataField
          label={t('customBuydown.createDialog.costOn', { amount: formatCurrencyNoDecimals(COST_REFERENCE_AMOUNT) })}
          value={formatCurrencyNoDecimals(contribution * COST_REFERENCE_AMOUNT)}
        />
        <DataField
          label={t('customBuydown.createDialog.financingCap')}
          value={maxFinancingAmount ? formatCurrencyNoDecimals(maxFinancingAmount) : t('customBuydown.noCap')}
        />
        <DataField icon={Lock} label={t('customBuydown.adminFee')} value={`${formatPercentage(ADMIN_FEE_RATE * 100)}%`} />
      </Box>

      <ContributionCard control={control} contribution={contribution} context={context} />

      <RatePreview merchantId={merchantId} control={control} context={context} />

      <ControlledNumberField
        control={control}
        name="maxFinancingAmount"
        label={t('customBuydown.maxFinancingAmount')}
        helperText={t('customBuydown.maxHelper')}
        isMoney
        sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}
      />

      <Box>
        <FieldSectionTitle>{t('customBuydown.createDialog.whenItAppliesTitle')}</FieldSectionTitle>
        <Caption sx={{ display: 'block', mt: 0.5, mb: 2 }}>
          {t('customBuydown.createDialog.whenItAppliesHint', { newPlanCode: previewPlanCode(contribution) })}
        </Caption>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
          <ControlledDateOnlyPicker
            control={control}
            name="activeFrom"
            label={t('customBuydown.createDialog.newPlanStarts')}
            slotProps={{ textField: { helperText: t('customBuydown.createDialog.activeFromHelper') } }}
            fullWidth
          />
          <ControlledDateOnlyPicker
            control={control}
            name="expiresOn"
            label={t('customBuydown.createDialog.newPlanEndsLabel')}
            slotProps={{
              textField: {
                helperText: t(
                  active || scheduled
                    ? 'customBuydown.createDialog.newPlanEndsHelper'
                    : 'customBuydown.createDialog.newPlanEndsHelperOptional',
                ),
              },
            }}
            fullWidth
          />
        </Box>
        <PlanTimelineCard active={active} scheduled={scheduled} activeFrom={activeFrom} expiresOn={expiresOn} />
      </Box>
    </Stack>
  )
}

export const useCreatePlanDialog = (merchantId: string, timeline: CustomBuydownTimeline) => {
  const { t } = useTranslation()
  const createPlan = useCreateCustomBuydownPlan(merchantId)
  // Read here, not in the content: dialog content mounts under DialogProvider, outside MerchantProvider.
  const context = useBorrowerContext()

  return useFormDialog<CreatePlanFormValues, CustomBuydownPlan | null, CustomBuydownPlan>({
    icon: Percent,
    title: t('customBuydown.createDialog.title'),
    subtitle: (ctx) =>
      ctx.props ? t('customBuydown.createDialog.subtitleSucceeding', { planCode: ctx.props.label }) : undefined,
    maxWidth: 'md',
    schema: createPlanSchema(),
    defaultValues: () => ({
      contribution: 0.0499,
      activeFrom: DateOnly.today(),
      maxFinancingAmount: null,
      expiresOn: null,
    }),
    onError: buildCreatePlanErrorOverrides(timeline),
    content: (ctx) => (
      <CreatePlanDialogContent ctx={ctx} merchantId={merchantId} timeline={timeline} context={context} />
    ),
    successMessage: (saved) => t('customBuydown.createDialog.savedSuccess', { planCode: saved.label }),
    onSubmit: async (data) => {
      return createPlan.mutateAsync({
        contribution: data.contribution,
        activeFrom: toActiveFromWire(data.activeFrom),
        expiresOn: data.expiresOn ? formatUtcIsoDateTime(data.expiresOn.toDate()) : undefined,
        maxFinancingAmount: data.maxFinancingAmount ?? undefined,
      })
    },
  })
}
